import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../Hooks/axiosApi";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";

type Lender = {
  id?: number;
  lenderId?: number;
  lenderID?: number;
  loanCount?: number;
  lenderName: string;
  initial: string;
  firstName: string;
  surname: string;
  email: string;
  mobile: string;
};

const getLenderId = (lender: Lender): number | null => {
  return lender.id ?? lender.lenderId ?? lender.lenderID ?? null;
};

const CRMLenders = () => {
  const navigate = useSafeNavigate();
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedLender, setSelectedLender] = useState<Lender | null>(null);

  const fetchLenders = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/Lenders/Getlenders");
      const apiData = response?.data;
      const list = Array.isArray(apiData) ? apiData : [];
      list.sort((a: Lender, b: Lender) => 
        (a.lenderName || "").localeCompare(b.lenderName || "")
      );
      setLenders(list);
    } catch (apiError: any) {
      setError(apiError?.response?.data?.message || "Failed to fetch lenders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLenders();
  }, []);

  const deleteLender = async (lender: Lender) => {
    const id = getLenderId(lender);
    if (!id) {
      setError("Cannot delete lender because id was not found.");
      toast.error("Cannot delete lender because id was not found.");
      return;
    }

    setDeleting(true);
    setError("");
    try {
      await api.delete(`/api/Lenders/Deletelenders/${id}`);
      await fetchLenders();
      toast.success("Lender deleted successfully.");
      setDeleteModal(false);
      setSelectedLender(null);
    } catch (apiError: any) {
      const message = apiError?.response?.data?.message || "Failed to delete lender.";
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="px-4 pt-4"
      style={{
        minHeight: "500px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
      }}
    >
      <div
        style={{
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          padding: "16px",
          backgroundColor: "#fff",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="f-w-900 text-primary m-0">Lenders</h3>
          <Button color="primary" onClick={() => navigate("/AddLender")}>
            Add Lender
          </Button>
        </div>

        {!!error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Lender Name</th>
                  <th>Loans</th>
                  <th>Initial</th>
                  <th>First Name</th>
                  <th>Surname</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th style={{ width: "170px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lenders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center">
                      No lenders found.
                    </td>
                  </tr>
                ) : (
                  lenders.map((lender, index) => (
                    <tr key={getLenderId(lender) ?? index}>
                      <td>{getLenderId(lender) ?? "-"}</td>
                      <td>{lender.lenderName}</td>
                      <td>{lender.loanCount}</td>
                      <td>{lender.initial}</td>
                      <td>{lender.firstName}</td>
                      <td>{lender.surname}</td>
                      <td>{lender.email}</td>
                      <td>{String(lender.mobile ?? "").replace(/\s+/g, "")}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              const lenderId = getLenderId(lender);
                              if (!lenderId) {
                                setError("Cannot edit lender because id was not found.");
                                toast.error("Cannot edit lender because id was not found.");
                                return;
                              }
                              navigate(`/EditLender/${lenderId}`);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedLender(lender);
                              setDeleteModal(true);
                            }}
                            disabled={deleting}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Delete Lender</ModalHeader>
        <ModalBody>
          Are you sure you want to delete <strong>{selectedLender?.lenderName || "this lender"}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={() => {
              if (selectedLender) {
                deleteLender(selectedLender);
              }
            }}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CRMLenders;


